import { type RefObject, useCallback } from "react";
import type { GapRow } from "@/pages/accounts/account-explorer/touchpoints-table/types";

/**
 * Enhanced scroll-to-date hook that works with virtualised tables.
 *
 * When `rowVirtualizer` is provided we call `scrollToIndex` directly.
 * It now also returns the date range of the target item.
 */

// Define a minimal interface for the virtualizer properties used
interface MinimalVirtualizer {
  scrollToIndex: (
    index: number,
    opts?: {
      align?: "start" | "center" | "end" | "auto";
      behavior?: "smooth" | "auto";
    },
  ) => void;
}

// Shared interface for items with date/id/endDate
interface HasDateAndId {
  date: string;
  id: string;
  endDate?: string;
  kind?: string;
}

// Helper function to poll for an element and execute a callback
function pollForElement(
  selectorFn: () => HTMLElement | null,
  callback: (element: HTMLElement) => void,
  interval = 100,
  timeout = 1500,
) {
  const startTime = Date.now();
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const attempt = () => {
    const element = selectorFn();
    if (element) {
      if (intervalId) clearInterval(intervalId);
      callback(element);
    } else if (Date.now() - startTime > timeout) {
      if (intervalId) clearInterval(intervalId);
      // console.warn("Polling timed out for element");
    }
  };

  intervalId = setInterval(attempt, interval);
  attempt(); // Initial attempt immediately
}

export function useScrollToDate(
  tableRef: RefObject<
    HTMLDivElement & { scrollTo: (options: ScrollToOptions) => void }
  >,
  data: HasDateAndId[],
  rowVirtualizer?: MinimalVirtualizer,
): (targetDate: string) => { start: string; end: string } | null {
  const scrollToDateFn = useCallback(
    (targetDate: string): { start: string; end: string } | null => {
      // If we have a virtualizer we can translate date -> index and scroll efficiently.
      if (rowVirtualizer && data.length > 0) {
        const targetTs = new Date(targetDate).getTime();

        let chosenIndex = -1;
        let closestPastIdx = -1;
        let closestFutureIdx = -1;
        let smallestPastDiff = Infinity;
        let smallestFutureDiff = Infinity;

        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          if (!item || typeof item.date !== "string") continue;

          const start = new Date(item.date).getTime();
          const end =
            item.endDate && typeof item.endDate === "string"
              ? new Date(item.endDate).getTime()
              : start;

          if (Number.isNaN(start) || Number.isNaN(end)) continue;

          // If the target date falls within (start,end) choose immediately
          if (targetTs >= start && targetTs <= end) {
            chosenIndex = i;
            break;
          }

          const pastDiff = targetTs - start; // positive if start before target
          const futureDiff = start - targetTs; // positive if start after target

          // Track closest past index (largest date before target)
          if (
            !Number.isNaN(pastDiff) &&
            pastDiff >= 0 &&
            pastDiff < smallestPastDiff
          ) {
            smallestPastDiff = pastDiff;
            closestPastIdx = i;
          }

          // Track closest future index (smallest date after target)
          if (
            !Number.isNaN(futureDiff) &&
            futureDiff >= 0 &&
            futureDiff < smallestFutureDiff
          ) {
            smallestFutureDiff = futureDiff;
            closestFutureIdx = i;
          }
        }

        // Preference: exact/in-range > future >= past
        if (chosenIndex === -1) {
          chosenIndex =
            closestFutureIdx !== -1 ? closestFutureIdx : closestPastIdx;
        }

        if (chosenIndex !== -1) {
          // If the chosen index lands on a gap row, adjust to next non-gap row
          while (
            chosenIndex < data.length &&
            (data[chosenIndex] as GapRow).kind === "gap"
          ) {
            chosenIndex++;
          }

          const targetItemId = data[chosenIndex]?.id;
          if (!targetItemId) return null; // Safety check

          rowVirtualizer.scrollToIndex(chosenIndex, {
            align: "center",
            behavior: "auto",
          });

          // Poll for the row element after initiating smooth scroll
          pollForElement(
            () =>
              tableRef.current?.querySelector(
                `tbody tr[data-row-id="${targetItemId}"]`,
              ) as HTMLElement | null,
            (rowEl) => {
              let targetElementToHighlight = rowEl;
              const previousElement = rowEl.previousElementSibling;

              // Check if the row immediately before this one is a gap row
              if (previousElement?.getAttribute("data-gap-row") === "true") {
                let nextSibling =
                  rowEl.nextElementSibling as HTMLElement | null;
                // Find the next non-gap row sibling
                while (
                  nextSibling &&
                  nextSibling.getAttribute("data-gap-row") === "true"
                ) {
                  nextSibling =
                    nextSibling.nextElementSibling as HTMLElement | null;
                }

                if (nextSibling && nextSibling.tagName === "TR") {
                  targetElementToHighlight = nextSibling;
                }
              }

              // Highlight the determined target element
              targetElementToHighlight.style.transition =
                "background-color 0.3s ease";
              targetElementToHighlight.style.backgroundColor =
                "hsl(212 63% 91%)"; // Light blue using HSL
              setTimeout(() => {
                // Check if element still exists before removing style
                if (tableRef.current?.contains(targetElementToHighlight)) {
                  targetElementToHighlight.style.backgroundColor = "";
                }
              }, 1000); // Keep highlight duration
            },
            100, // Check every 100ms
            1500, // Max wait 1.5 seconds
          );

          // Calculate and return the range of the target item (immediately)
          const targetItem = data[chosenIndex];
          const startDateString = targetItem.date;
          const endDateString = targetItem.endDate || targetItem.date; // Use endDate if available, else start date

          // Validate dates before returning
          if (startDateString && endDateString) {
            return { start: startDateString, end: endDateString };
          } else {
            console.error(
              "Invalid date strings found for target item",
              targetItem,
            );
            return null;
          }
        }
      }
      // If no virtualizer, no data, or index not found, return null
      return null;
    },
    [tableRef, data, rowVirtualizer],
  );

  return scrollToDateFn;
}
